import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ProductImage, StorefrontFamily } from "../types";

export function usePrefersReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}

/** Each variation's own photo, once, in variant order (images arrive primary-first). */
export function variantPhotos(family: StorefrontFamily): ProductImage[] {
  const seen = new Set<string>();
  const photos: ProductImage[] = [];
  for (const variant of family.variants) {
    const image = variant.images[0];
    if (image && !seen.has(image.url)) { seen.add(image.url); photos.push(image); }
  }
  return photos;
}

// Card picture for a product family. With several variations it cycles through
// their photos so shoppers see the options without tapping; it pauses while
// hovered and stays on the first photo for people who ask for reduced motion.
export function CardImage({ family, fallback, name, href }: { family: StorefrontFamily; fallback: ProductImage | null; name: string; href: string }) {
  const photos = family.variants.length > 1 ? variantPhotos(family) : [];
  const images = photos.length > 1 ? photos : fallback ? [fallback] : [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = usePrefersReducedMotion();
  const count = images.length;
  // Stagger cards so they do not all change at the same moment.
  const period = 3200 + (family.id.length % 5) * 350;
  useEffect(() => {
    if (count < 2 || paused || reduced) return;
    const timer = window.setInterval(() => setIndex((value) => (value + 1) % count), period);
    return () => window.clearInterval(timer);
  }, [count, paused, reduced, period]);
  const current = count ? index % count : 0;
  return (
    <a className="product-image" href={href} aria-label={`View ${name}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {images.map((image, position) => (
        <img key={image.url} className={position === current ? "active" : ""} src={image.url} alt={position === current ? image.alt || name : ""} aria-hidden={position !== current} loading="lazy" />
      ))}
      {count > 1 ? <span className="variant-count">{count} options</span> : null}
    </a>
  );
}

// Favourites on phones: one swipeable row that advances on its own and pauses
// while touched, hovered or focused. Wider screens show the balanced grid.
export function FavouritesStrip({ children }: { children: ReactNode }) {
  const strip = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    if (paused || reduced) return;
    const timer = window.setInterval(() => {
      const element = strip.current;
      if (!element || !window.matchMedia("(max-width: 560px)").matches) return;
      const card = element.firstElementChild as HTMLElement | null;
      if (!card) return;
      const step = card.offsetWidth + parseFloat(getComputedStyle(element).columnGap || "0");
      const atEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 4;
      element.scrollTo({ left: atEnd ? 0 : element.scrollLeft + step, behavior: "smooth" });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [paused, reduced]);
  const hold = () => setPaused(true);
  const release = () => window.setTimeout(() => setPaused(false), 6000);
  return (
    <div className="product-grid favourite-grid" ref={strip} onPointerDown={hold} onPointerUp={release} onMouseEnter={hold} onMouseLeave={() => setPaused(false)} onFocus={hold} onBlur={release}>
      {children}
    </div>
  );
}
